// NodosScreen.jsx

import React, { useEffect, useState, useCallback, useMemo } from "react";

import { Auth } from "@supabase/auth-ui-react";
import { supabase } from "../supabaseClient";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { Container, Typography } from "@mui/material";

import NodosBoard from "../components/Nodos/NodosBoard";
import LogOut from "../components/Nodos/LogOut";
import GoogleAuth, {
    upsertUsersStoreRefresh,
} from "../components/Nodos/GoogleAuth";
import ContactsDrawer from "../components/Nodos/ContactsDrawer";
import {
    LS_GOOGLE_ACCESS,
    LS_GOOGLE_REFRESH,
} from "../components/Nodos/helpers";
import CreateFab from "../components/Nodos/CreateFab";
import PersonaDrawer from "../components/Nodos/PersonaDrawer";
import SearchLocateBar from "../components/Nodos/SearchLocateBar";
import LayoutModeFab from "../components/Nodos/LayoutModeFab";

const NodosScreen = () => {
    const [session, setSession] = useState(null);
    const [personas, setPersonas] = useState([]);
    const [filterRules, setFilterRules] = useState({
        include: { groups: [] },
        exclude: { groups: [] },
    });
    const [relaciones, setRelaciones] = useState([]);
    const [grupos, setGrupos] = useState([]);
    const [gruposPersonas, setGruposPersonas] = useState([]);
    const [selectedPersonaId, setSelectedPersonaId] = useState("");
    const [locateTick, setLocateTick] = useState(0);

    const [fabOpenKey, setFabOpenKey] = useState(0);
    const [presetA, setPresetA] = useState("");
    const [presetB, setPresetB] = useState("");

    const [personaPanelOpen, setPersonaPanelOpen] = useState(false);
    const [layoutMode, setLayoutMode] = useState("groups");

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session); // keep existing var name
            if (session?.provider_token) {
                localStorage.setItem(LS_GOOGLE_ACCESS, session.provider_token);
                if (session?.provider_refresh_token && session?.user?.id) {
                    upsertUsersStoreRefresh(
                        session.user.id,
                        session.provider_refresh_token
                    );
                }
            }
            if (session?.provider_refresh_token) {
                localStorage.setItem(
                    LS_GOOGLE_REFRESH,
                    session.provider_refresh_token
                );
            }
        });
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((event, session) => {
            setSession(session);
            if (
                (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") &&
                session?.provider_token
            ) {
                localStorage.setItem(LS_GOOGLE_ACCESS, session.provider_token);
                if (session?.provider_refresh_token && session?.user?.id) {
                    upsertUsersStoreRefresh(
                        session.user.id,
                        session.provider_refresh_token
                    );
                }
            }
            if (event === "SIGNED_IN" && session?.provider_refresh_token) {
                localStorage.setItem(
                    LS_GOOGLE_REFRESH,
                    session.provider_refresh_token
                );
            }
            if (event === "SIGNED_OUT") {
                localStorage.removeItem(LS_GOOGLE_ACCESS);
                localStorage.removeItem(LS_GOOGLE_REFRESH);
            }
        });
        return () => subscription.unsubscribe();
    }, []);

    const loadSupabaseData = useCallback(async () => {
        if (!session) return;

        // PERSONAS
        const { data: personas, error: errP } = await supabase
            .from("Persona")
            .select(
                "persona_id, owner_id, display_name, googleResourceName, googlePicURL, googleEtag"
            )
            .eq("owner_id", session.user.id);

        if (errP) {
            console.error("Error personas:", errP);
            setPersonas([]);
            setRelaciones([]);
            return;
        }

        setPersonas(personas || []);
        const ids = (personas || []).map((p) => p.persona_id);

        // RELACIONES
        if (ids.length > 0) {
            const list = ids.map((x) => `"${x}"`).join(",");
            const { data: rels, error: errR } = await supabase
                .from("RelacionesPersonas")
                .select(
                    "persona_a, persona_b, tipo, reciproca, relacion_principal"
                )
                .or(`persona_a.in.(${list}),persona_b.in.(${list})`);
            if (errR) {
                console.error("Error relaciones:", errR);
                setRelaciones([]);
            } else {
                setRelaciones(rels || []);
            }
        } else {
            setRelaciones([]);
        }

        // GRUPOS
        const { data: gs, error: errG } = await supabase
            .from("Grupos")
            .select("grupo_id, nombre, color, parent, icon")
            .eq("owner_id", session.user.id)
            .order("grupo_id", { ascending: true });

        if (errG) {
            console.error("Error grupos:", errG);
            setGrupos([]);
        } else {
            setGrupos(gs || []);
        }

        // GRUPO-PERSONAS
        if (ids.length > 0) {
            const list = ids.map((x) => `"${x}"`).join(",");
            const { data: gps, error: errGP } = await supabase
                .from("GruposPersonas")
                .select("persona_id, grupo_id, grupo_principal")
                .in("persona_id", ids);
            if (errGP) {
                console.error("Error grupos-personas:", errGP);
                setGruposPersonas([]);
            } else {
                setGruposPersonas(gps || []);
            }
        } else {
            setGruposPersonas([]);
        }
    }, [session]);

    useEffect(() => {
        if (session) loadSupabaseData();
    }, [session, loadSupabaseData]);

    // useEffect(() => {
    //     console.log({ grupos, gruposPersonas });
    // }, [grupos, gruposPersonas]);

    function filterData({ personas, relaciones, gruposPersonas }, rules) {
        const includeSet = new Set(rules?.include?.groups?.map(String) || []);
        const excludeSet = new Set(rules?.exclude?.groups?.map(String) || []);

        // comment: construir mapa persona -> set de grupos
        const groupsByPersona = new Map();
        for (const gp of gruposPersonas || []) {
            const pid = String(gp.persona_id);
            const gid = String(gp.grupo_id);
            if (!groupsByPersona.has(pid)) groupsByPersona.set(pid, new Set());
            groupsByPersona.get(pid).add(gid);
        }

        // comment: decide si una persona entra por include
        const passesInclude = (pid) => {
            if (includeSet.size === 0) return true; // comment: sin include => todos
            const gs = groupsByPersona.get(pid);
            if (!gs) return false;
            for (const g of gs) {
                if (includeSet.has(g)) return true;
            }
            return false;
        };

        // comment: decide si una persona cae por exclude
        const failsExclude = (pid) => {
            if (excludeSet.size === 0) return false;
            const gs = groupsByPersona.get(pid);
            if (!gs) return false;
            for (const g of gs) {
                if (excludeSet.has(g)) return true;
            }
            return false;
        };

        // comment: filtrar personas
        const visiblePersonaIds = new Set();
        const personasView = (personas || []).filter((p) => {
            const pid = String(p.persona_id);
            if (!passesInclude(pid)) return false;
            if (failsExclude(pid)) return false;
            visiblePersonaIds.add(pid);
            return true;
        });

        // comment: filtrar relaciones (ambos extremos visibles)
        const relacionesView = (relaciones || []).filter((r) => {
            const a = String(r.persona_a);
            const b = String(r.persona_b);
            return visiblePersonaIds.has(a) && visiblePersonaIds.has(b);
        });

        // comment: opcional - filtrar gruposPersonas a solo visibles
        const gruposPersonasView = (gruposPersonas || []).filter((gp) =>
            visiblePersonaIds.has(String(gp.persona_id))
        );

        return { personasView, relacionesView, gruposPersonasView };
    }

    const { personasView, relacionesView, gruposPersonasView } = useMemo(
        () => filterData({ personas, relaciones, gruposPersonas }, filterRules),
        [personas, relaciones, gruposPersonas, filterRules]
    );

    if (!session) {
        return (
            <Container maxWidth="xs" style={{ marginTop: "2rem" }}>
                <br />
                <br />
                <br />
                <Typography variant="h4" align="center" gutterBottom>
                    Nodos
                </Typography>
                {/* <Auth supabaseClient={supabase} appearance={{ theme: ThemeSupa }} /> */}
                <GoogleAuth />
            </Container>
        );
    }

    return (
        <>
            <NodosBoard
                personas={personasView}
                relaciones={relacionesView}
                grupos={grupos}
                gruposPersonas={gruposPersonas}
                selectedPersonaId={selectedPersonaId}
                onSelectPersona={(id) => setSelectedPersonaId(String(id))}
                onConnectPair={(a, b) => {
                    setPresetA(String(a));
                    setPresetB(String(b));
                    setFabOpenKey((k) => k + 1);
                }}
                onOpenPersona={() => setPersonaPanelOpen(true)}
                locateTick={locateTick}
                layoutMode={layoutMode}
            />
            <LogOut afterSignOut={() => {}} fabNumber={0} />
            <ContactsDrawer
                personas={personas}
                selectedPersonaId={selectedPersonaId}
                onLinked={loadSupabaseData}
                onLocatePersona={(id) => {
                    setSelectedPersonaId(String(id));
                    setLocateTick((t) => t + 1);
                }}
                fabNumber={1}
            />
            <LayoutModeFab
                layoutMode={layoutMode}
                onToggle={() =>
                    setLayoutMode((m) => (m === "groups" ? "grid" : "groups"))
                }
                fabNumber={2}
            />
            <CreateFab
                personas={personas}
                relaciones={relaciones}
                selectedPersonaId={selectedPersonaId}
                onCreated={loadSupabaseData}
                externalOpenKey={fabOpenKey}
                presetPersonaA={presetA}
                presetPersonaB={presetB}
                grupos={grupos}
                fabNumber={3}
            />
            <PersonaDrawer
                open={personaPanelOpen}
                onClose={() => setPersonaPanelOpen(false)}
                personaId={selectedPersonaId}
                personas={personas}
                relaciones={relaciones}
                grupos={grupos}
                gruposPersonas={gruposPersonas}
                onChanged={loadSupabaseData}
            />
            <SearchLocateBar
                personas={personasView}
                relaciones={relacionesView}
                grupos={grupos}
                gruposPersonas={gruposPersonas}
                onLocatePersona={(id) => {
                    setSelectedPersonaId(String(id));
                    setLocateTick((t) => t + 1);
                }}
                onFilterRulesChange={(rules) => setFilterRules(rules)}
                filterRules={filterRules}
            />
        </>
    );
};

export default NodosScreen;
